import { useState, useEffect } from "react";
import { Button } from "src/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "src/components/ui/dialog";
import { Loader2 } from "lucide-react";
import { useCreateInvoiceGroupMutation, useGetInvoiceGroupPreviewQuery, useGetCompaniesQuery } from "../../../redux/billing/billing.endpoints";
import { InvoiceGroup } from "src/pages/billing/types/billing.types";
import InvoiceGroupPreviewTable from "./invoice-group-preview-table";

interface NewInvoiceGroupModalProps {
  isOpen: boolean;
  onClose: () => void;
  existingInvoiceGroups: InvoiceGroup[];
  onCreated?: (newGroupId: string, wasGenerated?: boolean) => void;
  previewGroupId?: string; // Optional prop to open directly in preview mode
}

const NewInvoiceGroupModal = ({ isOpen, onClose, existingInvoiceGroups, onCreated, previewGroupId }: NewInvoiceGroupModalProps) => {
  const [selectedMonth, setSelectedMonth] = useState<number>(() => {
    const now = new Date();
    const previousMonth = now.getMonth() === 0 ? 12 : now.getMonth();
    return previousMonth;
  });
  const [selectedYear, setSelectedYear] = useState<number>(() => {
    const now = new Date();
    const previousMonth = now.getMonth() === 0 ? 12 : now.getMonth();
    return previousMonth === 12 ? now.getFullYear() - 1 : now.getFullYear();
  });
  const [createdGroupId, setCreatedGroupId] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);

  const [createInvoiceGroup, { isLoading: isCreating, error }] = useCreateInvoiceGroupMutation();

  const { data: previewData, isLoading: isLoadingPreview, isFetching: isFetchingPreview, refetch: refetchPreview } = useGetInvoiceGroupPreviewQuery(
    createdGroupId!,
    { skip: !createdGroupId || !showPreview }
  );

  const { data: companies = [] } = useGetCompaniesQuery();

  // Effect to handle opening directly in preview mode
  useEffect(() => {
    if (isOpen && previewGroupId) {
      setCreatedGroupId(previewGroupId);
      setShowPreview(true);
    } else if (!isOpen) {
      // Reset state when modal closes
      setCreatedGroupId(null);
      setShowPreview(false);
    }
  }, [isOpen, previewGroupId]);

  const isDuplicate = existingInvoiceGroups.some(
    (group) => group.month === selectedMonth && group.year === selectedYear
  );

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: currentYear - 2025 + 1 }, (_, i) => 2025 + i);

  const handlePreview = async() => {
    if (isDuplicate) return;

    try {
      // First call: Create invoice group with isInitial=true
      const result = await createInvoiceGroup({
        month: selectedMonth,
        year: selectedYear,
        isInitial: true,
      }).unwrap();
      setCreatedGroupId(result.id);
      setShowPreview(true);
    } catch (err) {
      console.error('Failed to create invoice group:', err);
    }
  };

  const handleClose = () => {
    if (!isCreating && !isFetchingPreview) {
      onClose();
      setCreatedGroupId(null);
      setShowPreview(false);
    }
  };

  const handleComplete = async() => {
    if (!createdGroupId) return;

    try {
      // Second call: Trigger actual invoice generation with isInitial=false
      // Fire and forget - don't await this call
      createInvoiceGroup({
        month: selectedMonth,
        year: selectedYear,
        isInitial: false,
      }).unwrap().catch(err => {
        console.error('Failed to generate invoices:', err);
      });

      // Close modal immediately and trigger the original completion flow
      onClose();
      if (onCreated && createdGroupId) {
        onCreated(createdGroupId, true); // true indicates generation was initiated
      }
      // Reset state for next use
      setCreatedGroupId(null);
      setShowPreview(false);
    } catch (err) {
      console.error('Failed to start invoice generation:', err);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={handleClose}
    >
      <DialogContent className={`${showPreview ? 'sm:max-w-[1000px] max-h-[90vh]' : 'sm:max-w-[450px]'} bg-white`}>
        <DialogHeader>
          <DialogTitle>
            {showPreview ? 'Review Invoice Group Mappings' : 'New Invoice Group'}
          </DialogTitle>
          <DialogDescription>
            {showPreview
              ? 'Review and assign companies to the detected mappings for your invoice group.'
              : 'Create a new invoice group for the selected month and year.'
            }
          </DialogDescription>
        </DialogHeader>

        {!showPreview ? (
          <>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">
                    Month
                  </label>
                  <select
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                    disabled={isCreating}
                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  >
                    {monthNames.map((month, index) => (
                      <option
                        key={index + 1}
                        value={index + 1}
                      >
                        {month}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">
                    Year
                  </label>
                  <select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                    disabled={isCreating}
                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  >
                    {years.map((year) => (
                      <option
                        key={year}
                        value={year}
                      >
                        {year}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {isDuplicate && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <svg
                        className="h-4 w-4 text-red-400"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-red-800">
                        An invoice group for
                        {' '}
                        {monthNames[selectedMonth - 1]}
                        {' '}
                        {selectedYear}
                        {' '}
                        already exists.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <svg
                        className="h-4 w-4 text-red-400"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-red-800">
                        Failed to create invoice group. Please try again.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={handleClose}
                disabled={isCreating}
              >
                Cancel
              </Button>
              <Button
                onClick={handlePreview}
                disabled={isDuplicate || isCreating}
                className="bg-green-1000 text-white border-green-1000 hover:bg-green-1000/90"
              >
                {isCreating ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Preview'
                )}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <div className="py-4 max-h-[calc(90vh-200px)] overflow-y-auto">
              {isFetchingPreview ? (
                <div className="flex justify-center items-center py-8">
                  <div className="text-center">
                    <Loader2 className="w-8 h-8 animate-spin text-blue-500 mx-auto mb-4" />
                    <div className="text-gray-500">
                      Assigning invoices to companies...
                    </div>
                  </div>
                </div>
              ) : previewData ? (
                <InvoiceGroupPreviewTable
                  previewData={previewData}
                  companies={companies}
                  onRefresh={refetchPreview}
                />
              ) : (
                <div className="text-center py-8 text-gray-500">
                  No preview data available.
                </div>
              )}
            </div>

            <DialogFooter>
              <Button
                onClick={handleComplete}
                disabled={isFetchingPreview}
                className="bg-green-1000 text-white border-green-1000 hover:bg-green-1000/90"
              >
                {isFetchingPreview ? 'Loading...' : 'Create'}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default NewInvoiceGroupModal;